/**
 * Service-name → inline SVG icon mapping.
 *
 * The generator's SYSTEM_PROMPT tells the LLM to draw literal, service-relevant
 * icons (snowflake for AC, lightning bolt for wiring, dripping tap for
 * leaks, etc.). This module gives the intake wizard a *preview* of the icon
 * that will accompany each ticked service, so the owner can eyeball their
 * choices before generating.
 *
 * We use inline SVG (no external font/CDN) so this works even without
 * network access. Keep icons minimal, one-colour, 24×24.
 *
 * Detection is keyword-based on the service label, matched against the
 * same mental model the LLM uses.
 */

// Ordered list — first match wins. Keywords are lowercase substrings.
const ICON_RULES = [
  // Air conditioning / cooling
  { match: ['air con', 'aircon', 'hvac', 'split system', 'ducted', 're-gas', 'regas', 'refriger'], name: 'snowflake' },

  // Electrical
  { match: ['electrical fault', 'fault find', 'wiring', 'switchboard'], name: 'bolt' },
  { match: ['power point', 'outlet'], name: 'outlet' },
  { match: ['led', 'lighting', 'light install'], name: 'bulb' },
  { match: ['safety switch', 'rcd', 'circuit break'], name: 'circuit' },
  { match: ['smoke alarm', 'smoke detect'], name: 'alarm' },
  { match: ['ev charg', 'ev install', 'battery'], name: 'battery' },
  { match: ['solar'], name: 'sun' },

  // Plumbing
  { match: ['blocked drain', 'unblock', 'drain clear'], name: 'plunger' },
  { match: ['hot water'], name: 'water-heater' },
  { match: ['leak', 'burst pipe'], name: 'drip' },
  { match: ['tap', 'toilet'], name: 'tap' },
  { match: ['gas fit', 'gas cook', 'gas hot', 'gas leak', 'gas appl', 'lpg'], name: 'flame' },

  // General emergency
  { match: ['emergency'], name: 'siren' },

  // Cleaning
  { match: ['carpet clean'], name: 'brush' },
  { match: ['window clean'], name: 'window' },
  { match: ['bond', 'end of lease', 'end-of-lease'], name: 'sparkle' },
  { match: ['office clean', 'house clean', 'regular clean'], name: 'spray' },

  // Detailing
  { match: ['interior detail'], name: 'car-seat' },
  { match: ['exterior detail', 'paint correction', 'ceramic', 'mobile detail'], name: 'car-polish' },

  // Landscaping / lawn
  { match: ['lawn', 'mowing', 'mow'], name: 'grass' },
  { match: ['garden design', 'turf', 'irrigation'], name: 'sprout' },
  { match: ['hedge', 'tree trim', 'tree'], name: 'tree' },
  { match: ['retaining wall'], name: 'bricks' },

  // Handyman / builder
  { match: ['assembly', 'furniture', 'general repair', 'door', 'lock'], name: 'wrench' },
  { match: ['paint'], name: 'paint' },
  { match: ['fence'], name: 'fence' },
  { match: ['gutter'], name: 'gutter' },
  { match: ['extension', 'new build', 'renovation', 'reno', 'bathroom', 'kitchen', 'structural', 'decks', 'pergola'], name: 'hammer' },
];

const FALLBACK = 'wrench';

const SVG_MAP = {
  snowflake: (
    <path d="M12 2v20M4.93 4.93l14.14 14.14M2 12h20M4.93 19.07L19.07 4.93M8 6l4 4 4-4M8 18l4-4 4 4M6 8l-4 4 4 4M18 8l4 4-4 4" />
  ),
  bolt: (<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />),
  outlet: (<><rect x="5" y="4" width="14" height="16" rx="2" /><circle cx="9" cy="10" r="1.2" /><circle cx="15" cy="10" r="1.2" /><path d="M12 14v3" /></>),
  bulb: (<><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-4 10.5c1 1 1.5 2 1.5 3v.5h5V17c0-1 .5-2 1.5-3A6 6 0 0 0 12 3z" /></>),
  circuit: (<><rect x="3" y="7" width="18" height="10" rx="1.5" /><path d="M12 7v10M8 12h8" /></>),
  alarm: (<><circle cx="12" cy="12" r="8" /><path d="M12 8v4M12 15h.01" /></>),
  battery: (<><rect x="3" y="8" width="16" height="8" rx="1.5" /><path d="M19 11v2M6 11v2M10 11v2" /></>),
  sun: (<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>),
  plunger: (<><ellipse cx="12" cy="6" rx="6" ry="2.5" /><path d="M12 8.5V22M9 22h6" /></>),
  'water-heater': (<><rect x="6" y="3" width="12" height="18" rx="2" /><path d="M9 8h6M9 12h6M12 16v3" /></>),
  drip: (<path d="M12 3s-6 8-6 12a6 6 0 0 0 12 0c0-4-6-12-6-12z" />),
  tap: (<><path d="M6 10h6v6H6zM12 13h4a2 2 0 0 1 2 2v3" /><path d="M8 6v4M6 6h4" /></>),
  flame: (<path d="M12 3s5 5 5 10a5 5 0 0 1-10 0c0-2 1-3 2-4 0 1 .5 2 1.5 2C10 8 12 6 12 3z" />),
  siren: (<><path d="M7 20h10M8 20v-6a4 4 0 0 1 8 0v6" /><path d="M6 12h1M17 12h1M12 6V4M8.5 7.5L7 6M15.5 7.5L17 6" /></>),
  brush: (<><rect x="4" y="14" width="16" height="6" rx="1" /><path d="M8 14V8h8v6M10 4h4" /></>),
  window: (<><rect x="4" y="4" width="16" height="16" rx="1" /><path d="M12 4v16M4 12h16" /></>),
  sparkle: (<><path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" /><path d="M18 15l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" /></>),
  spray: (<><path d="M9 6h6M12 6V3" /><rect x="7" y="8" width="10" height="12" rx="1.5" /><path d="M17 10h3M17 13h3" /></>),
  'car-seat': (<><path d="M6 20V9a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v11" /><path d="M6 16h10" /></>),
  'car-polish': (<><path d="M4 15l2-5h12l2 5" /><path d="M4 15h16v3H4z" /><circle cx="7" cy="18" r="1.5" /><circle cx="17" cy="18" r="1.5" /><path d="M18 8l2-2M20 6l1 1" /></>),
  grass: (<path d="M4 20c1-6 3-9 4-9 0 3-1 6-1 9M9 20c1-6 3-9 4-9 0 3-1 6-1 9M14 20c1-6 3-9 4-9 0 3-1 6-1 9" />),
  sprout: (<><path d="M12 20V10" /><path d="M12 10c-3 0-5-2-5-5 3 0 5 2 5 5zM12 10c3 0 5-2 5-5-3 0-5 2-5 5z" /></>),
  tree: (<><path d="M12 3l5 7h-3l4 6h-4l3 5H7l3-5H6l4-6H7z" /><path d="M12 21v-3" /></>),
  bricks: (<><rect x="3" y="6" width="18" height="12" /><path d="M3 12h18M9 6v6M15 12v6" /></>),
  wrench: (<path d="M15 3a5 5 0 0 0-4.9 6L3 16.1V21h4.9l7.1-7.1A5 5 0 0 0 22 8l-3 3-2-2 3-3a5 5 0 0 0-5-3z" />),
  paint: (<><rect x="3" y="4" width="18" height="6" rx="1" /><path d="M12 10v3h5v4h-3v4h-4v-4H7v-4h5z" /></>),
  fence: (<><path d="M4 20V8l2-3 2 3v12M10 20V8l2-3 2 3v12M16 20V8l2-3 2 3v12" /><path d="M4 12h16M4 16h16" /></>),
  gutter: (<><path d="M3 8h18M5 8v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" /><path d="M8 12v3M12 12v5M16 12v3" /></>),
  hammer: (<><path d="M15 2l7 7-3 3-4-4-3 3-3-3z" /><path d="M11 8l-8 8v4h4l8-8" /></>),
};

/**
 * Resolve the icon slug for a service label.
 */
export function serviceIconName(label) {
  const s = String(label || '').toLowerCase();
  for (const rule of ICON_RULES) {
    if (rule.match.some((m) => s.includes(m))) return rule.name;
  }
  return FALLBACK;
}

/**
 * Render the SVG element for a service label.
 * Returns a self-contained <svg> — style with `color:` on the parent.
 */
export function serviceIconSvg(label, size = 20) {
  const name = serviceIconName(label);
  const glyph = SVG_MAP[name] || SVG_MAP[FALLBACK];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph}
    </svg>
  );
}
